import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import WaveSurfer from 'wavesurfer.js';

@Component({
  selector: 'app-audio-player-modal',
  templateUrl: './audio-player-modal.page.html',
  styleUrls: ['./audio-player-modal.page.scss'],
})
export class AudioPlayerModalPage implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('waveform', { static: false }) waveformRef: ElementRef;

  /** MP3 檔案 URL */
  @Input() mp3Url: string;

  /** 顯示標題（檔案名稱） */
  @Input() title: string;

  /** 狀態標籤資訊 */
  @Input() statusInfo: { label: string; status: string };

  private wavesurfer: WaveSurfer | null = null;

  isLoading = true;
  isPlaying = false;
  currentTime = '0:00';
  duration = '0:00';

  constructor(private modalController: ModalController) {}

  ngOnInit() {}

  ngAfterViewInit() {
    this.initWaveSurfer();
  }

  ngOnDestroy() {
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
      this.wavesurfer = null;
    }
  }

  /**
   * 初始化 WaveSurfer
   */
  private initWaveSurfer() {
    if (!this.waveformRef?.nativeElement || !this.mp3Url) {
      console.error('Waveform container or mp3Url not available');
      this.isLoading = false;
      return;
    }

    this.wavesurfer = WaveSurfer.create({
      container: this.waveformRef.nativeElement,
      waveColor: '#a8d5ba', // 淺綠色波形（未播放部分）
      progressColor: '#4caf50', // 深綠色進度（已播放部分）
      cursorColor: '#2e7d32', // 游標顏色
      barWidth: 3,
      barGap: 2,
      barRadius: 3,
      height: 80,
      normalize: true,
      backend: 'WebAudio',
    });

    // 事件監聽
    this.wavesurfer.on('ready', () => {
      this.isLoading = false;
      this.duration = this.formatTime(this.wavesurfer.getDuration());
      // 自動播放
      this.wavesurfer.play();
      this.isPlaying = true;
    });

    this.wavesurfer.on('audioprocess', () => {
      this.currentTime = this.formatTime(this.wavesurfer.getCurrentTime());
    });

    this.wavesurfer.on('seeking', () => {
      this.currentTime = this.formatTime(this.wavesurfer.getCurrentTime());
    });

    this.wavesurfer.on('play', () => {
      this.isPlaying = true;
    });

    this.wavesurfer.on('pause', () => {
      this.isPlaying = false;
    });

    this.wavesurfer.on('finish', () => {
      this.isPlaying = false;
    });

    this.wavesurfer.on('error', (error) => {
      console.error('WaveSurfer error:', error);
      this.isLoading = false;
    });

    // 載入音檔
    this.wavesurfer.load(this.mp3Url);
  }

  /**
   * 播放/暫停切換
   */
  togglePlay() {
    if (!this.wavesurfer) return;

    if (this.isPlaying) {
      this.wavesurfer.pause();
    } else {
      this.wavesurfer.play();
    }
  }

  /**
   * 格式化時間（秒 → m:ss）
   */
  private formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * 關閉 Modal
   */
  close() {
    // 停止播放
    if (this.wavesurfer && this.isPlaying) {
      this.wavesurfer.pause();
    }
    this.modalController.dismiss();
  }
}
